/*
 * usf-override-custom.js
 *
 * Takes the FIRST ROW of USF product cards out of the app's lazyload path on a
 * small set of landing collections, so the largest-contentful image is fetched
 * as soon as USF has rendered the grid instead of waiting for a lazyload pass.
 *
 * Scope is deliberately narrow:
 *  - first row only, decided from real layout geometry; every later row keeps
 *    stock USF lazyloading,
 *  - main image only (the first <img> in .card-media); hover / swap images are
 *    never touched, so they stay lazy and off the initial connection,
 *  - fetchpriority="high" goes on exactly one image per page - the first card
 *    of the first row, i.e. the LCP candidate,
 *  - every other page keeps stock USF behaviour, and this file never touches
 *    assets/usf.js, assets/usf-custom.js or the USF dashboard code,
 *  - it runs ONCE. The first pass that promotes a row disconnects the
 *    observer, because every pass costs a forced layout and USF keeps
 *    mutating the page long after the grid is up.
 *
 * It runs entirely after render, from the MutationObserver below. USF calls
 * _usfGetImgAttrs(img, imgUrl, scaledImageUrl) once for the main image and
 * once for the hover image, with no index and no flag to tell them apart, so
 * an attribute-level wrapper cannot express "first row, main image only" -
 * the DOM is the only place where that distinction exists.
 *
 * Notes on the two things that used to break images here:
 *  - USF hands out image URLs as *templates* containing "{size}"
 *    (see _usfGetOriginImgWithSize(url, '{size}x') in usf.js). Appending
 *    "_350x" to one produced "..._{size}x_350x.jpg", which 404s.
 *  - When lazyloading is enabled USF returns `data-srcset` (no `src`) plus the
 *    "lazyload" class, and component-card.css renders `.card-media > img.lazyload`
 *    at opacity 0. Setting a `src` without dropping that class leaves the image
 *    invisible, so both are handled together.
 */
(function () {
    'use strict';

    if (window.__usfOverrideCustomLoaded) return;
    window.__usfOverrideCustomLoaded = true;

    /* ------------------------------------------------------------------ *
     * Target pages
     * ------------------------------------------------------------------ */

    // Normalised (lowercase, no trailing slash) collection paths.
    var TARGET_PATHS = [
        '/collections/bagues-femme',
        '/es/collections/anillos-mujer',
        '/it/collections/anelli-donna',
        '/de/collections/ringe-dame',
        '/pl/collections/bagues-femme',
        '/en/collections/bagues-femme',
        '/collections/saint-valentin',
        '/es/collections/saint-valentin',
        '/it/collections/saint-valentin',
        '/de/collections/saint-valentin',
        '/pl/collections/saint-valentin',
        '/en/collections/saint-valentin'
    ];

    function normalizePathname(pathname) {
        if (typeof pathname !== 'string' || !pathname) return '';

        var path = pathname;
        try {
            path = decodeURIComponent(path);
        } catch (e) {
            // malformed escape sequence: keep the raw value
        }

        // location.pathname carries neither query nor hash, but be explicit so
        // the helper is safe to call with a fuller string too.
        path = path.split('?')[0].split('#')[0].toLowerCase();
        path = path.replace(/\/+$/, '');

        return path || '/';
    }

    // Any collection page, with or without a locale prefix, and nothing else:
    //   /collections/colliers             -> yes
    //   /es/collections/collares          -> yes
    //   /collections/colliers/products/x  -> no (product page; [^/]+ at the end excludes it)
    //
    // Paging, filtering and currency live in the query string, so they never affect
    // detection.
    //
    // WHY THIS WAS WIDENED (4 September)
    // ----------------------------------
    // TARGET_PATHS above only ever covered /collections/bagues-femme and
    // /collections/saint-valentin. Every other collection kept stock USF behaviour.
    // Measured on /collections/colliers: the first card carried loading="lazy" and
    // fetchPriority="auto", and its image was served at _1200x for a 196x196 render.
    // Lighthouse's LCP checklist failed on both priorityHinted and eagerlyLoaded, LCP
    // sat at 10.4 s and cost roughly 25 points of the mobile score.
    //
    // Widening changes only WHICH pages are treated, never what is done to each one:
    // the scope of this file stays exactly as narrow as the header describes - first
    // row only, main image only, one fetchpriority per page, and the observer
    // disconnects after its first pass.
    //
    // TARGET_PATHS is kept above for the record; it is no longer consulted.
    var COLLECTION_PATH_RE = new RegExp('^(?:/[a-z]{2})?/collections/[^/]+$');

    function isTargetCollectionPage(pathname) {
        var path = normalizePathname(pathname);
        if (!path) return false;
        return COLLECTION_PATH_RE.test(path);
    }

    function onTargetPage() {
        return !!(window.location && isTargetCollectionPage(window.location.pathname));
    }

    /* ------------------------------------------------------------------ *
     * Shopify image URL handling
     * ------------------------------------------------------------------ */

    var IMAGE_EXTENSIONS = 'jpg|jpeg|png|webp|gif';

    // "<base>[_<variant>].<ext>" where <variant> is 800x800 / 800x / x800.
    var VARIANT_RE = new RegExp(
        '^(.*?)(?:_(\\d+x\\d+|\\d+x|x\\d+))?(\\.(?:' + IMAGE_EXTENSIONS + '))$',
        'i'
    );

    // Suffixes a plain "_350x" swap cannot rewrite safely.
    var UNSAFE_SUFFIX_RE = new RegExp(
        '(?:_crop_[a-z]+|@[0-9]+x)\\.(?:' + IMAGE_EXTENSIONS + ')$',
        'i'
    );

    function parseUrl(url) {
        try {
            var base = (window.location && window.location.href) ? window.location.href : undefined;
            return new URL(url, base);
        } catch (e) {
            return null;
        }
    }

    // A URL we are willing to hand to the browser as-is.
    function isUsableUrl(url) {
        if (typeof url !== 'string') return false;

        var trimmed = url.trim();
        if (!trimmed) return false;
        if (trimmed.indexOf('{') !== -1 || trimmed.indexOf('}') !== -1) return false;

        return parseUrl(trimmed) !== null;
    }

    // Only Shopify-served product images accept "_<size>x" variants. Theme
    // assets (/assets/) and third-party hosts must be left untouched.
    function isShopifyImageUrl(parsed) {
        if (!parsed) return false;

        var host = (parsed.hostname || '').toLowerCase();
        var path = parsed.pathname || '';

        if (path.indexOf('/assets/') !== -1) return false;

        if (host === 'cdn.shopify.com') return true;
        if (/(^|\.)shopify\.com$/.test(host)) return true;
        if (/(^|\.)myshopify\.com$/.test(host)) return true;
        if (/(^|\.)shopifycdn\.(com|net)$/.test(host)) return true;

        // Images served from the shop's own domain.
        return path.indexOf('/cdn/shop/') === 0 || path.indexOf('/cdn/shopifycloud/') === 0;
    }

    /**
     * Convert a Shopify image URL to its 350px variant.
     * Returns '' when the conversion cannot be done safely, so the caller can
     * fall back to the untouched original rather than emit a broken URL.
     */
    function toShopify350(rawUrl) {
        if (typeof rawUrl !== 'string') return '';

        var url = rawUrl.trim();
        if (!url) return '';

        // USF size templates: "_{size}x" -> "_350x". Anything else left in
        // braces is not something we can resolve.
        if (url.indexOf('{') !== -1 || url.indexOf('}') !== -1) {
            url = url.replace(/\{\s*(?:size|width)\s*\}/gi, '350');
            if (url.indexOf('{') !== -1 || url.indexOf('}') !== -1) return '';
        }

        if (!isShopifyImageUrl(parseUrl(url))) return '';

        // Split off query string / hash and put them back untouched.
        var cut = url.search(/[?#]/);
        var path = cut === -1 ? url : url.slice(0, cut);
        var suffix = cut === -1 ? '' : url.slice(cut);

        if (UNSAFE_SUFFIX_RE.test(path)) return '';

        var parts = path.match(VARIANT_RE);
        if (!parts || !parts[1]) return '';

        // Already the size we want (either as _350x or _350x350).
        if (parts[2] && /^350x(?:350)?$/i.test(parts[2])) return url;

        return parts[1] + '_350x' + parts[3] + suffix;
    }

    /* ------------------------------------------------------------------ *
     * First-row promotion
     * ------------------------------------------------------------------ */

    // Only cards USF itself rendered. The theme's own grids (recently viewed,
    // cross-sells) also use .card-media and must keep stock behaviour.
    var CARD_SELECTOR = '.usf-sr-product__image-container .card-media';

    // How far apart two cards' top edges may be and still count as one grid
    // row. Rows sit a full card height apart (~400 px), so this only has to
    // absorb sub-pixel layout noise.
    var ROW_TOLERANCE_PX = 16;

    // Safety net for the frame where the grid is in the DOM but not yet laid
    // out and every card reports the same top: never promote more than one
    // row's worth of cards on the widest layout USF renders.
    var MAX_FIRST_ROW = 6;

    var normalizeScheduled = false;
    var observer = null;

    function requestFrame(callback) {
        if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(callback);
        } else {
            window.setTimeout(callback, 16);
        }
    }

    function setIfChanged(element, property, value) {
        if (element[property] !== value) element[property] = value;
    }

    // Attributes rather than properties for the URL-bearing ones: img.src
    // reflects an absolute URL, so comparing it against a protocol-relative
    // "//cdn.shopify.com/..." would never match, every pass would rewrite it,
    // and rewriting src can restart a load.
    function setAttrIfChanged(element, name, value) {
        if (element.getAttribute(name) !== value) element.setAttribute(name, value);
    }

    // First URL of a srcset string ("<url> 200w,<url> 400w" -> "<url>").
    function firstSrcsetUrl(srcset) {
        if (typeof srcset !== 'string' || !srcset) return '';

        var first = srcset.split(',')[0];
        if (!first) return '';

        return first.trim().split(/\s+/)[0] || '';
    }

    /**
     * The `sizes` value to advertise for a card image.
     *
     * USF emits a static expression - "(min-width: 1100px) <intrinsic>px,
     * (min-width: 750px) calc((100vw - 130px) / 2), calc((100vw - 50px) / 2)" -
     * and this file used to replace it with a flat "350px". Neither describes
     * the grid: the card renders at ~213 CSS px on a 412 px viewport, not at
     * 350 px and not at the intrinsic width of the source file. Measuring the
     * element is the only way to be right for whatever column count the layout
     * actually produced.
     */
    function displayWidthPx(img, card) {
        var width = 0;

        if (img && typeof img.getBoundingClientRect === 'function') {
            width = img.getBoundingClientRect().width;
        }
        if (!width && card && typeof card.getBoundingClientRect === 'function') {
            width = card.getBoundingClientRect().width;
        }

        width = Math.round(width);

        return (width > 0 ? width : 350) + 'px';
    }

    /**
     * Paint the promoted image without the card's fade-in.
     *
     * component-card.css puts `transition: all .8s ease` on .card-media > img
     * and USF inserts the element carrying the "lazyload" class, which is
     * `opacity: 0`. Dropping that class therefore starts an 800 ms fade from
     * fully transparent - and a fully transparent element is not an LCP
     * candidate, so the fade is charged straight to "element render delay".
     *
     * The suppression is inline, applies only to promoted images, and is
     * lifted once the image has painted so hover behaviour is unchanged.
     */
    function paintWithoutFade(img) {
        if (img.__usfNoFade) return;
        img.__usfNoFade = true;

        var previous = img.style.transition;
        img.style.transition = 'none';

        function restore() {
            img.removeEventListener('load', restore);
            img.removeEventListener('error', restore);

            // One frame later: the "none" has to survive the frame that paints
            // the image, otherwise the fade simply starts a tick later.
            requestFrame(function () {
                img.style.transition = previous || '';
            });
        }

        if (img.complete) restore();
        else {
            img.addEventListener('load', restore);
            img.addEventListener('error', restore);
        }
    }

    /**
     * Take one image out of USF's lazyload path.
     *
     * Idempotent by construction: every value is derived from what is already
     * on the element, and the URL rewrite is skipped once the browser has
     * selected a source, so a second pass cannot trigger a second download.
     *
     * `sizes` arrives pre-measured: this function only writes, so that a pass
     * cannot invalidate layout and then read it back. See normalizeCardImages.
     */
    function promoteImage(img, sizes, isLcp) {
        // Loading has already begun - either we promoted this element on an
        // earlier pass, or USF's lazyloader got to it first. Touching src,
        // srcset or sizes now risks selecting a second candidate and fetching
        // the image twice, so only the priority hints are set.
        var started = !!img.currentSrc;

        if (!started) {
            // While lazyloading is on USF keeps the candidates in data-srcset
            // and leaves src unset; with it off they are on srcset/src.
            var candidates = img.getAttribute('data-srcset') || img.getAttribute('srcset') || '';
            var source = firstSrcsetUrl(candidates) ||
                img.getAttribute('data-src') ||
                img.getAttribute('src') || '';

            var url = toShopify350(source);

            if (!url) {
                // No safe 350px variant: keep a valid original URL rather than
                // inventing one.
                if (isUsableUrl(source)) url = source.trim();
            }

            // Nothing usable at all - leave USF's markup exactly as it was.
            if (!url) return false;

            setAttrIfChanged(img, 'src', url);
            setAttrIfChanged(img, 'srcset', url + ' 350w');
            setAttrIfChanged(img, 'sizes', sizes);

            // data-srcset is deliberately left in place: USF's lazyloader keys
            // off the class, and if anything does still reach this element it
            // then re-assigns an equivalent srcset instead of an empty one.
            var className = img.getAttribute('class');
            if (typeof className === 'string') {
                var stripped = className
                    .replace(/\blazyload(?:ing|ed)?\b/g, '')
                    .replace(/\s+/g, ' ')
                    .trim();

                if (stripped !== className) setAttrIfChanged(img, 'class', stripped);
            }

            paintWithoutFade(img);
        }

        setIfChanged(img, 'loading', 'eager');

        // Exactly one image per page carries the hint - anything more and the
        // LCP image shares a throttled connection with its own grid.
        if (isLcp) setIfChanged(img, 'fetchPriority', 'high');

        return true;
    }

    // Cards whose top edge is level with the highest card on the page, in DOM
    // order. Geometry rather than a hardcoded column count, so the same code
    // holds for the 2-up mobile grid and the 4-up desktop one.
    function firstRowCards(cards) {
        var visible = [];
        var i, rect;

        for (i = 0; i < cards.length; i++) {
            rect = cards[i].getBoundingClientRect();

            // Skip anything not laid out (display:none, collapsed filters).
            if (!rect.width && !rect.height) continue;

            visible.push({ card: cards[i], top: rect.top });
        }

        if (!visible.length) return [];

        var minTop = visible[0].top;
        for (i = 1; i < visible.length; i++) {
            if (visible[i].top < minTop) minTop = visible[i].top;
        }

        var row = [];
        for (i = 0; i < visible.length && row.length < MAX_FIRST_ROW; i++) {
            if (visible[i].top - minTop <= ROW_TOLERANCE_PX) row.push(visible[i].card);
        }

        return row;
    }

    // Only the main image of each first-row card is touched. In USF's card
    // template (_usfImageProductCard in usf.js) the main <img> is emitted
    // before the hover/second one, so it is the first <img> in .card-media.
    // Returns true once the LCP candidate itself is dealt with, which is the
    // signal to stop watching the page.
    //
    // Split into a read phase and a write phase on purpose. Measuring, then
    // writing, then measuring again makes the browser flush layout once per
    // image: the mobile trace attributed a second forced reflow to this file
    // for exactly that reason. Every getBoundingClientRect() call now happens
    // before the first attribute is written, so the pass costs one layout.
    function normalizeCardImages() {
        if (!onTargetPage()) return false;

        var cards = document.querySelectorAll(CARD_SELECTOR);
        if (!cards || !cards.length) return false;

        // --- read phase: geometry only, no DOM writes -------------------
        var row = firstRowCards(cards);
        var targets = [];
        var i, main;

        for (i = 0; i < row.length; i++) {
            main = row[i].querySelector('img');
            if (!main) continue;

            targets.push({
                img: main,
                sizes: displayWidthPx(main, row[i]),
                isLcp: i === 0
            });
        }

        // --- write phase: no geometry reads ----------------------------
        var done = false;

        for (i = 0; i < targets.length; i++) {
            var promoted = promoteImage(targets[i].img, targets[i].sizes, targets[i].isLcp);
            if (targets[i].isLcp) done = promoted;
        }

        return done;
    }

    // Many USF DOM updates collapse into a single pass.
    function scheduleNormalize() {
        if (normalizeScheduled) return;
        normalizeScheduled = true;

        requestFrame(function () {
            normalizeScheduled = false;
            try {
                // firstRowCards() reads geometry, so every pass forces a
                // layout. USF, its plugins and the theme keep mutating the
                // page for seconds after the grid renders, so this must not
                // stay armed once the first row has been promoted.
                if (normalizeCardImages()) stopObserver();
            } catch (e) {
                // ignore
            }
        });
    }

    function startObserver() {
        if (!document.body || typeof window.MutationObserver !== 'function') return;

        // childList only: the attribute writes made by normalizeCardImages()
        // are therefore not observed and cannot feed back into another pass.
        observer = new MutationObserver(scheduleNormalize);

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false
        });
    }

    // After this the grid keeps stock USF behaviour: a later re-render
    // (filter, sort, paging) lazyloads its images the way the app intends.
    // The LCP has already been decided by then.
    function stopObserver() {
        if (!observer) return;

        observer.disconnect();
        observer = null;
    }

    /* ------------------------------------------------------------------ *
     * Bootstrap
     * ------------------------------------------------------------------ */

    function init() {
        if (!onTargetPage()) return;

        startObserver();
        scheduleNormalize();
    }

    try {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }
    } catch (e) {
        // fail silently
    }
})();
